import * as React from 'react';
import { LocationAccumulationSummary } from '../../../../types/reporting.type';
import { fetchLocationAccumulationsSummary } from '../../../../services/reporting';
import { FunctionComponent, useContext, useEffect } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import { formatDollar, formatPercent } from '../../../../helpers/formatters';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import styles from './styles';
import { Spacing } from '../../../Layout';
import { withStyles, WithStyles } from '@mui/styles';
import { InstallationProviderContext } from '../../../../application/Providers/Installation';

interface Props {
    locationIds: number[];
    submissionId: string;
    collapsable?: boolean;
    tivLabel?: string;
    showPercentOfBound?: boolean;
}

const LocationAccumulationSummaryTable: FunctionComponent<Props & WithStyles<typeof styles>> = (props) => {
    const { locationIds, submissionId, collapsable, classes, tivLabel = 'TIV', showPercentOfBound = true } = {
        ...props,
    };

    const installationProvider = useContext(InstallationProviderContext);

    const [accumulationSummary, setAccumulationSummary] = React.useState<
        Record<string, LocationAccumulationSummary[]> | LocationAccumulationSummary[]
    >(collapsable ? {} : []);
    const [expanded, setExpanded] = React.useState<boolean | string>(false);

    const fetchAccumulationSummary = async () => {
        const summary = await fetchLocationAccumulationsSummary(installationProvider, submissionId, locationIds);
        if (!collapsable) {
            setAccumulationSummary(summary);
        } else {
            const summaryDict = {};
            summary.forEach((entry) => {
                if (!(entry.groupType in summaryDict)) {
                    summaryDict[entry.groupType] = [];
                }
                summaryDict[entry.groupType].push(entry);
            });
            setAccumulationSummary(summaryDict);
        }
    };

    useEffect(() => {
        if (submissionId) {
            fetchAccumulationSummary();
        }
    }, [submissionId, locationIds]);

    const handleChange = (panel: string) => (event: any, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const buildAccumulationTable = (rows: LocationAccumulationSummary[]) => (
        <>
            <Table size={'small'}>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell align={'right'}>Bound {tivLabel}</TableCell>
                        <TableCell align={'right'}>Quoted {tivLabel}</TableCell>
                        {showPercentOfBound && <TableCell align={'right'}>Percent of Bound</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.groupType + row.groupName}>
                            <TableCell>{row.groupType}</TableCell>
                            <TableCell>{row.groupName}</TableCell>
                            <TableCell align={'right'}>{formatDollar(row.boundTiv, 0)}</TableCell>
                            <TableCell align={'right'}>{formatDollar(row.quotedTiv, 0)}</TableCell>
                            {showPercentOfBound && (
                                <TableCell align={'right'}>{formatPercent(row.boundTiv / row.totalBoundTiv)}</TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {rows.length === 0 && (
                <>
                    <Spacing height={'12px'} />
                    <Typography>
                        Currently there are no quoted or bound policies contributing to accumulations for this location.
                    </Typography>
                </>
            )}
        </>
    );

    return (
        <>
            {!collapsable &&
                accumulationSummary &&
                buildAccumulationTable(accumulationSummary as LocationAccumulationSummary[])}
            {collapsable &&
                accumulationSummary &&
                Object.keys(accumulationSummary).map((groupType) => (
                    <Accordion
                        style={{ width: '650px' }}
                        expanded={expanded === groupType}
                        onChange={handleChange(groupType)}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="fireResearch-header"
                        >
                            <Typography className={classes.heading}>{`${groupType} Accumulations`}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>{buildAccumulationTable(accumulationSummary[groupType])}</AccordionDetails>
                    </Accordion>
                ))}
        </>
    );
};

export default withStyles(styles)(LocationAccumulationSummaryTable);
